import api from '../services/api';

export const validatePhoneNumber = (phone) => api('ValidatePhoneNumber', { phoneNumber: phone });

export const formatUSPhoneNumber = (phoneNumberString) => {
  let match = phoneNumberString.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return `${intlCode}(${match[2]}) ${match[3]}-${match[4]}`;
  }

  // Only +1 is NANP. The country code has to be explicit here: an optional group
  // would also match a foreign number carrying exactly ten digits after the plus
  // and render it as a US number (and interpolate the non-participating group as
  // the literal string 'undefined').
  match = phoneNumberString.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phoneNumberString;
};

// eslint-disable-next-line security/detect-unsafe-regex
export const isValidFormattedPhoneNumber = (phoneNumber) => /^([+]?\d{1,2}[.\s-]?)?(\d{3}[.-]?){2}\d{4}$/.test(phoneNumber);

/**
 * Normalizes a stored phone value to the dashed national shape the scoring form
 * inputs use (###-###-####), matching what formatToPhone produces on keystroke.
 *
 * A leading '+' is treated as an authoritative country code: only '+1' is NANP
 * and can be reduced to a national number. Any other '+' value is returned
 * unchanged, because stripping the plus would turn a foreign number carrying ten
 * digits into a valid-looking US number that passes validation and persists.
 *
 * Without a '+' the country code is unknowable, so ten digits are treated as
 * NANP -- that is the whole point of the fix. A foreign number stored with no
 * plus is indistinguishable from a NANP number and cannot be rescued here.
 *
 * Anything that does not resolve to ten national digits is returned as its
 * digits, undashed and untruncated, so an unexpected value stays intact and
 * visibly fails validation rather than being reshaped into a different number.
 */
export const toDashedNationalPhone = (phoneNumber) => {
  if (!phoneNumber) return '';

  const value = `${phoneNumber}`.trim();
  const digits = value.replace(/[^0-9]/g, '');
  const isNanpWithCountryCode = digits.length === 11 && digits.startsWith('1');

  // An explicit non-NANP country code is preserved verbatim.
  if (value.startsWith('+') && !isNanpWithCountryCode) {
    return value;
  }

  const nationalDigits = isNanpWithCountryCode ? digits.slice(1) : digits;
  const match = nationalDigits.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match) return nationalDigits;

  return `${match[1]}-${match[2]}-${match[3]}`;
};

export const sanitizePhoneNumber = (phoneNumber) => {
  let workingNumber = `${phoneNumber}`.trim();

  // An explicit country code other than +1 is not NANP. The digits alone would
  // be re-prefixed with +1 below, emitting a different, valid-looking US number
  // and persisting it, so reject the value instead of reshaping it.
  if (workingNumber.startsWith('+') && !workingNumber.startsWith('+1')) {
    return null;
  }

  if (workingNumber.startsWith('+1')) {
    workingNumber = workingNumber.slice('+1'.length);
  }

  // The digits can never carry a leading '+' at this point, so the country code
  // is always added here (the previous startsWith('+') guard was unreachable).
  const sanitizedNumber = `+1${workingNumber.replace(/[^0-9]/g, '')}`;
  if (sanitizedNumber.length !== 12) return null;

  return sanitizedNumber;
};
